<template>
  <footer id="support" class="footer">
    <div class="footer__inner">
      <!-- Main Footer -->
      <div class="footer__main">
        <div class="footer__brand">
          <span class="footer__logo" aria-label="MODA">
            <BaseLogoModa :size="40" home-path="/order" />
          </span>
          <p class="footer__desc">
            글로벌 외환선물 투자 플랫폼. 강화된 보안과 24시간 전문 상담으로 모든 투자자 곁에
            함께해요.
          </p>
          <div class="footer__company">
            <p class="footer__company-name">Orbis Securities (Pty) Ltd.</p>
            <p class="footer__company-address">
              18 Cavendish Road, Claremont,<br />
              Cape Town, Western Cape 7708,<br />
              South Africa
            </p>
            <p class="footer__company-reg">등록번호 2024/224812/07</p>
          </div>
        </div>

        <div class="footer__links-wrap">
          <div v-for="group in footerLinkGroups" :key="group.title" class="footer__column">
            <h3 class="footer__column-title">{{ group.title }}</h3>
            <ul class="footer__links">
              <li v-for="link in group.links" :key="link.label">
                <router-link v-if="isNavLink(link)" :to="link.href" class="footer__link">
                  {{ link.label }}
                </router-link>
                <button
                  v-else
                  type="button"
                  class="footer__link footer__link--button"
                  :disabled="link.disabled"
                  @click="handleFooterLinkClick($event, link)"
                >
                  {{ link.label }}
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Divider -->
      <div class="footer__divider" />

      <!-- Legal Notice -->
      <div class="footer__legal">
        <p class="footer__legal-text">
          <span class="footer__legal-label">투자 위험 고지:</span>
          본 서비스는 투자 원금의 손실 가능성이 있는 금융투자상품입니다. 과거의 투자 실적이 미래의
          수익을 보장하지 않으며, 투자 결정은 고객님의 판단과 책임 하에 이루어져야 합니다. 투자 전
          상품의 특성과 위험성을 충분히 확인하시기 바랍니다. 추가적인 문의는 고객지원팀(
          <a href="mailto:support@orbissecurities.com" class="footer__legal-link"
            >support@orbissecurities.com</a
          >
          ) 또는 전화 +61) 2-7247-2203 으로 연락주시기 바랍니다.
        </p>
      </div>

      <div class="footer__bottom">
        <p class="footer__copyright">© 2026 MODA. All rights reserved.</p>
      </div>
    </div>

    <TermsDialog
      v-model:is-open="isTermsDialogOpen"
      v-model:selected-tab="selectedTermsTab"
      :show-footer-actions="false"
      @agree="handleTermsAgree"
      @close="isTermsDialogOpen = false"
    />

    <CustomTermsDialog
      v-model:is-open="isOrderExecutionPolicyDialogOpen"
      title="주문실책정책"
      :terms-options="[]"
      :content="ORDER_EXECUTION_POLICY_CONTENT"
      :footer-buttons="1"
    />
  </footer>
</template>

<script setup lang="ts">
import { ORDER_EXECUTION_POLICY_CONTENT } from '@/constants/orderExecutionPolicyContent';
import CustomTermsDialog from '@/components/common/dialog/CustomTermsDialog.vue';
import TermsDialog from '@/components/auth/signup/dialog/TermsDialog.vue';
import { BaseLogoModa } from '@template/ui';
import { ref } from 'vue';

interface FooterLinkItem {
  label: string;
  href: string;
  termsDialogKey?: string;
  orderExecutionPolicyDialog?: boolean;
  disabled?: boolean;
}

interface FooterLinkGroup {
  title: string;
  links: FooterLinkItem[];
}

const footerLinkGroups: FooterLinkGroup[] = [
  {
    title: '서비스',
    links: [
      { label: '외환', href: '/order' },
      { label: '해외선물', href: '/order' },
      { label: '상품선물', href: '/order' },
      { label: '가상화폐', href: '/order' },
    ],
  },
  {
    title: '고객 지원',
    links: [
      { label: '공지사항', href: '/support/notice' },
      { label: '이벤트', href: '/support/event' },
      { label: '1:1 문의', href: '/support/qna' },
      { label: '자주 묻는 질문', href: '/support/faq' },
      { label: '이용가이드', href: '/support/guide' },
    ],
  },
  {
    title: '법적 정보',
    links: [
      { label: '이용약관', href: '#', termsDialogKey: 'terms-of-use' },
      { label: '개인정보처리방침', href: '#', termsDialogKey: 'privacy-policy' },
      { label: '위험공시안내', href: '#', termsDialogKey: 'risk-disclosure' },
      { label: '주문실책정책', href: '#', orderExecutionPolicyDialog: true },
    ],
  },
];

const isTermsDialogOpen = ref(false);
const selectedTermsTab = ref('');
const isOrderExecutionPolicyDialogOpen = ref(false);

function isNavLink(link: FooterLinkItem): boolean {
  return link.href.startsWith('/');
}

function handleFooterLinkClick(_event: MouseEvent, link: FooterLinkItem) {
  if (link.disabled) return;
  if (link.orderExecutionPolicyDialog) {
    isOrderExecutionPolicyDialogOpen.value = true;
    return;
  }
  if (link.termsDialogKey) {
    selectedTermsTab.value = link.termsDialogKey;
    isTermsDialogOpen.value = true;
  }
}

function handleTermsAgree() {
  isTermsDialogOpen.value = false;
}
</script>

<style scoped>
.footer {
  background-color: #0a0a0a; /* moda-dark-900 */
}

.footer__inner {
  max-width: 80rem;
  margin-left: auto;
  margin-right: auto;
  padding-left: 1rem;
  padding-right: 1rem;
}

@media (min-width: 640px) {
  .footer__inner {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
}

@media (min-width: 1024px) {
  .footer__inner {
    padding-left: 2rem;
    padding-right: 2rem;
  }
}

.footer__main {
  padding-top: 3rem;
  padding-bottom: 3rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

@media (min-width: 768px) {
  .footer__main {
    padding-top: 4rem;
    padding-bottom: 4rem;
  }
}

@media (min-width: 1024px) {
  .footer__main {
    flex-direction: row;
    align-items: flex-start;
  }
}

.footer__brand {
  max-width: 100%;
  flex-shrink: 0;
}

@media (min-width: 1024px) {
  .footer__brand {
    width: 50%;
    max-width: 280px;
  }
}

.footer__links-wrap {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  width: 100%;
  min-width: 0;
}

@media (min-width: 1024px) {
  .footer__links-wrap {
    width: auto;
    min-width: 18rem;
    margin-left: auto;
    flex-shrink: 0;
  }
}

.footer__logo {
  display: inline-block;
  margin-bottom: 1rem;
}

.footer__desc {
  font-size: 0.875rem;
  line-height: 1.625;
  color: #9ca3af;
}

.footer__company {
  margin-top: 1rem;
}

.footer__company > * + * {
  margin-top: 0.125rem;
}

.footer__company-name,
.footer__company-address,
.footer__company-reg {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.footer__company-address {
  font-weight: inherit;
  line-height: 1.625;
}

.footer__column-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #fff;
  margin-bottom: 1rem;
}

.footer__links {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.footer__link {
  font-size: 0.875rem;
  color: #9ca3af;
  transition: color 0.2s;
}

.footer__link:hover {
  color: #fff;
}

.footer__link--button {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
}

.footer__link--button:disabled {
  cursor: default;
  opacity: 0.6;
}

.footer__divider {
  border-top: 1px solid #3a3a3a; /* moda-dark-600 */
}

.footer__legal {
  padding-top: 1.5rem;
  padding-bottom: 1.5rem;
}

.footer__legal-text {
  font-size: 0.75rem;
  color: #6b7280;
  line-height: 1.625;
}

.footer__legal-label {
  font-weight: 600;
  color: #9ca3af;
}

.footer__legal-link:hover {
  color: #d1d5db;
  transition: color 0.2s;
}

.footer__bottom {
  border-top: 1px solid #3a3a3a; /* moda-dark-600 */
  padding-top: 1.5rem;
  padding-bottom: 1.5rem;
}

.footer__copyright {
  font-size: 0.75rem;
  color: #6b7280;
}
</style>
